import { FC, useState, useEffect, MouseEvent } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  OrderFilterFormValues,
  OrderFilterValues,
  OrderViewData,
} from 'src/models/Management.interface';
import { useFormik } from 'formik';
import MainLayout from 'src/pages/private';
import SearchBar from 'src/components/InputFields/Searchbar';
import OrderGrid from 'src/components/Grids/OrderGrid';
import FilterPopover from 'src/components/StyledComponents/FilterPopover';
import CreatedDatePicker from 'src/components/InputFields/DatePicker';
import FormikSelectField, {
  Option,
} from 'src/components/InputFields/FormikSelectField';
import { routeURL, apiUrls } from 'src/utils/properties.utils';
import { getCustomerListBasic } from 'src/services/customer.service';
import { Customer } from 'src/models/Management.interface';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import ViewOrderDialog from 'src/components/DialogBox/ViewOrderDialog/ViewOrderDialog';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import {
  PrimaryActionButton,
  ButtonIcon,
  FilterButton,
  FilterIcon,
  FilterPopoverIcon,
  FilterPopoverTitle,
  FilterPopoverDivider,
  FilterCloseIcon,
  FilterLabel,
  SmallActionButton,
  SpacedContainer,
} from 'src/components/StyledComponents/StyledPageComponents';

const OrderManagement: FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSnackbar } = StyledSnackbar();

  const [orderAdded, setOrderAdded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderViewData | null>(
    null
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [dateFilterType, setDateFilterType] = useState<string>('createdAt');
  const [filterValues, setFilterValues] = useState<OrderFilterValues>({
    customerId: '',
    status: '',
    scheduledStartDate: null,
    scheduledEndDate: null,
    actualStartDate: null,
    actualEndDate: null,
    createdStartDate: null,
    createdEndDate: null,
  });
  const [customers, setCustomers] = useState<Customer[]>([]);

  // Handle view parameter from URL to open ViewOrderDialog
  useEffect(() => {
    const viewOrderId = searchParams.get('view');
    if (viewOrderId) {
      const fetchAndOpenViewDialog = async () => {
        try {
          const response = await axiosTokenInstance.get(
            `${apiUrls.GET_ORDER_BY_ID}/${viewOrderId}`
          );
          setSelectedOrder(response.data);
          setViewDialogOpen(true);
        } catch (error) {
          const errorMessage = await getApiErrorMessage(error);
          showSnackbar(errorMessage, 'error');
          // Remove the view parameter if order fetch fails
          searchParams.delete('view');
          setSearchParams(searchParams);
        }
      };
      fetchAndOpenViewDialog();
    }
  }, [searchParams]);

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedOrder(null);
    // Remove the view parameter from URL
    searchParams.delete('view');
    setSearchParams(searchParams);
  };

  const handleOrderUpdated = () => {
    setOrderAdded((prev) => !prev);
  };

  const customerOptions: Option[] = customers.map((customer) => ({
    label: customer.name,
    value: customer.id,
  }));

  const statusOptions: Option[] = [
    { label: 'New', value: 'NEW' },
    { label: 'Pending Approval', value: 'PENDING_APPROVAL' },
    { label: 'Approved', value: 'APPROVED' },
    { label: 'Rejected', value: 'REJECTED' },
    { label: 'Payment Pending', value: 'PAYMENT_PENDING' },
    { label: 'Payment Completed', value: 'PAYMENT_COMPLETED' },
    { label: 'Completed', value: 'COMPLETED' },
  ];

  const dateFilterOptions: Option[] = [
    { label: 'Created At', value: 'createdAt' },
    { label: 'Scheduled Delivery Date', value: 'scheduledDeliveryDate' },
    { label: 'Actual Delivery Date', value: 'actualDeliveryDate' },
  ];

  const getDateFilterLabel = () => {
    switch (dateFilterType) {
      case 'scheduledDeliveryDate':
        return 'Scheduled Delivery Date:';
      case 'actualDeliveryDate':
        return 'Actual Delivery Date:';
      case 'createdAt':
      default:
        return 'Created at:';
    }
  };

  const handleSearchChange = (newQuery: string) => {
    setSearchTerm(newQuery);
  };

  const handleClickOpen = () => {
    navigate(routeURL.USER + routeURL.ORDER_CREATE);
  };

  const handleFilterClick = async (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setFilterOpen(true);
    try {
      const customerList = await getCustomerListBasic();
      setCustomers(customerList || []);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      setCustomers([]);
    }
  };

  const handleClose = () => {
    filterFormik.resetForm();
    setDateFilterType('createdAt');
    setAnchorEl(null);
    setFilterOpen(false);
  };

  const handleClear = () => {
    filterFormik.resetForm();
    setDateFilterType('createdAt');
    setFilterValues({
      customerId: '',
      status: '',
      scheduledStartDate: null,
      scheduledEndDate: null,
      actualStartDate: null,
      actualEndDate: null,
      createdStartDate: null,
      createdEndDate: null,
    });
  };

  const filterFormik = useFormik<OrderFilterFormValues>({
    initialValues: {
      customerPicker: '',
      statusPicker: '',
      scheduledStartDate: null,
      scheduledEndDate: null,
      actualStartDate: null,
      actualEndDate: null,
      createdStartDate: null,
      createdEndDate: null,
    },
    onSubmit: (values) => {
      // Map the date values based on selected filter type
      const filterData: OrderFilterValues = {
        customerId: values.customerPicker,
        status: values.statusPicker,
        scheduledStartDate: null,
        scheduledEndDate: null,
        actualStartDate: null,
        actualEndDate: null,
        createdStartDate: null,
        createdEndDate: null,
      };

      // Set the appropriate date fields based on dateFilterType
      if (dateFilterType === 'scheduledDeliveryDate') {
        filterData.scheduledStartDate = values.scheduledStartDate;
        filterData.scheduledEndDate = values.scheduledEndDate;
      } else if (dateFilterType === 'actualDeliveryDate') {
        filterData.actualStartDate = values.actualStartDate;
        filterData.actualEndDate = values.actualEndDate;
      } else if (dateFilterType === 'createdAt') {
        filterData.createdStartDate = values.createdStartDate;
        filterData.createdEndDate = values.createdEndDate;
      }

      setFilterValues(filterData);
      setFilterOpen(false);
    },
  });

  const SearchBarComponent = () => (
    <SearchBar
      id="search"
      value={searchTerm}
      onChange={handleSearchChange}
      placeholder="Search by order number"
      width={isMobile ? '100%' : '280px'}
    />
  );

  const PrimaryActionButtonComponent = () => (
    <PrimaryActionButton
      className="btn-primary"
      width={isMobile ? '100%' : '140px'}
      onClick={handleClickOpen}
    >
      <ButtonIcon component="i" className="bx bx-plus 1" />
      New Order
    </PrimaryActionButton>
  );

  return (
    <MainLayout
      title="Order Management"
      searchBar={<SearchBarComponent />}
      button={<PrimaryActionButtonComponent />}
      filterButton={
        <>
          {/* Refactored inline sx from line 249 to ml prop of FilterButton */}
          <FilterButton
            className="btn-filter"
            width={isMobile ? '100%' : '100px'}
            onClick={handleFilterClick}
            ml={isMobile ? 0 : '16px'}
          >
            <FilterIcon component="i" className="bx bx-filter" />
            Add Filter
          </FilterButton>
        </>
      }
    >
      <FilterPopover
        open={filterOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
      >
        <Box>
          <Box display="flex" alignItems="center">
            <FilterPopoverIcon component="i" className="bx bx-filter" />
            <FilterPopoverTitle>Filter Data By</FilterPopoverTitle>
          </Box>
          <FilterPopoverDivider />
          <FilterCloseIcon
            component="i"
            className="bx bx-x-circle"
            onClick={handleClose}
          />
        </Box>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <FormikSelectField
            id="customerPicker"
            label="Customer"
            height="28px"
            width="150px"
            customMessage="Customer"
            textColor="var(--subtext)"
            borderColor="var(--hover-white)"
            fontSize="10px"
            fontWeight="400"
            showLabel={false}
            formikProps={filterFormik}
            options={customerOptions}
          />
          <FormikSelectField
            id="statusPicker"
            label="Status"
            height="28px"
            width="150px"
            customMessage="Status"
            textColor="var(--subtext)"
            borderColor="var(--hover-white)"
            fontSize="10px"
            fontWeight="400"
            showLabel={false}
            formikProps={filterFormik}
            options={statusOptions}
          />
        </Box>
        <Box mb={1}>
          {/* Refactored inline sx from line 304 to marginBottom prop of FilterLabel */}
          <FilterLabel marginBottom="8px">Select Date Filter</FilterLabel>
          <FormikSelectField
            id="dateFilterTypePicker"
            label="Date Filter"
            height="28px"
            width="100%"
            customMessage="Select Date Filter"
            textColor="var(--subtext)"
            borderColor="var(--hover-white)"
            fontSize="10px"
            fontWeight="400"
            showLabel={false}
            formikProps={{
              ...filterFormik,
              values: {
                ...filterFormik.values,
                dateFilterTypePicker: dateFilterType,
              },
              handleChange: (e: any) => {
                setDateFilterType(e.target.value);
              },
            }}
            options={dateFilterOptions}
          />
        </Box>
        <Box>
          <FilterLabel>{getDateFilterLabel()}</FilterLabel>
          <SpacedContainer display="flex" justifyContent="space-between">
            <CreatedDatePicker
              name={
                dateFilterType === 'scheduledDeliveryDate'
                  ? 'scheduledStartDate'
                  : dateFilterType === 'actualDeliveryDate'
                  ? 'actualStartDate'
                  : 'createdStartDate'
              }
              placeholder="Start Date"
              formikProps={filterFormik}
            />
            <CreatedDatePicker
              name={
                dateFilterType === 'scheduledDeliveryDate'
                  ? 'scheduledEndDate'
                  : dateFilterType === 'actualDeliveryDate'
                  ? 'actualEndDate'
                  : 'createdEndDate'
              }
              placeholder="End Date"
              formikProps={filterFormik}
            />
          </SpacedContainer>
        </Box>
        <Box display="flex" justifyContent="space-between" mt={2}>
          <SmallActionButton
            btnVariant="inactive"
            width="70px"
            onClick={handleClear}
          >
            Clear
          </SmallActionButton>
          <SmallActionButton
            className="btn-primary"
            width="70px"
            onClick={() => {
              filterFormik.handleSubmit();
              setAnchorEl(null);
            }}
          >
            Apply
          </SmallActionButton>
        </Box>
      </FilterPopover>
      <OrderGrid
        state={orderAdded}
        searchTerm={searchTerm}
        filters={filterValues}
      />
      <ViewOrderDialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        data={selectedOrder}
        onApprove={handleOrderUpdated}
        onReject={handleOrderUpdated}
        onRefresh={handleOrderUpdated}
        onOrderPayments={() => {
          if (selectedOrder?.id) {
            navigate(routeURL.ORDER_PAYMENTS_REDIRECT(selectedOrder.id), {
              state: { orderNumber: selectedOrder.orderNumber },
            });
          }
        }}
      />
    </MainLayout>
  );
};

export default OrderManagement;
